/**
 * Hero Carousel — Web Component
 * WR Beautiful K-Beauty Theme
 *
 * Features:
 * - Autoplay with configurable speed (3–8s)
 * - Pause on hover, focus, and via pause button
 * - Fade + text slide-up transitions (0.6s cubic-bezier)
 * - Touch swipe support (mobile)
 * - Keyboard navigation (arrow keys)
 * - Progress bar on active dot
 * - prefers-reduced-motion respected
 * - WAI-ARIA carousel pattern
 */

if (!customElements.get('hero-carousel')) {
  class HeroCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.progressRaf = null;
      this.progressStart = 0;
      this.isPaused = false;
      this.isUserPaused = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.swipeThreshold = 50;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hero-carousel__slide'));
      this.dots = Array.from(this.querySelectorAll('.hero-carousel__dot'));
      this.prevBtn = this.querySelector('.hero-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.hero-carousel__arrow--next');
      this.pauseBtn = this.querySelector('.hero-carousel__pause-btn');
      this.viewport = this.querySelector('.hero-carousel__viewport');
      this.liveRegion = this.querySelector('[aria-live]');

      if (this.slides.length <= 1) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseFloat(this.dataset.speed) * 1000 || 4500;

      this._bindEvents();
      this._goTo(0, true);

      if (this.autoplay && !this.reducedMotion) {
        this._startAutoplay();
      } else {
        this.isPaused = true;
        this._updatePauseBtn();
      }

      // Listen for reduced-motion changes
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._motionHandler = (e) => {
        this.reducedMotion = e.matches;
        if (e.matches) {
          this._stopAutoplay();
          this.isPaused = true;
          this._updatePauseBtn();
        }
      };
      this._motionQuery.addEventListener('change', this._motionHandler);

      // Shopify theme editor: jump to selected slide and pause autoplay while editing
      this._blockSelectHandler = (e) => {
        const slideIndex = this.slides.indexOf(e.target);
        if (slideIndex === -1) return;
        this.isUserPaused = true;
        this._pause();
        this._goTo(slideIndex);
      };
      this._blockDeselectHandler = (e) => {
        const slideIndex = this.slides.indexOf(e.target);
        if (slideIndex === -1) return;
        this.isUserPaused = false;
        if (this.autoplay && !this.reducedMotion) {
          this._resume();
        }
      };
      this.addEventListener('shopify:block:select', this._blockSelectHandler);
      this.addEventListener('shopify:block:deselect', this._blockDeselectHandler);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._motionQuery) {
        this._motionQuery.removeEventListener('change', this._motionHandler);
      }
      if (this._blockSelectHandler) {
        this.removeEventListener('shopify:block:select', this._blockSelectHandler);
      }
      if (this._blockDeselectHandler) {
        this.removeEventListener('shopify:block:deselect', this._blockDeselectHandler);
      }
    }

    _bindEvents() {
      // Arrow buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._next());
      }

      // Pause button
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => this._togglePause());
      }

      // Dot navigation
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._goTo(i));
      });

      // Hover pause
      this.addEventListener('mouseenter', () => {
        if (!this.isUserPaused) this._pause();
      });
      this.addEventListener('mouseleave', () => {
        if (!this.isUserPaused) this._resume();
      });

      // Focus pause
      this.addEventListener('focusin', () => {
        if (!this.isUserPaused) this._pause();
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget) && !this.isUserPaused) {
          this._resume();
        }
      });

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this._prev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this._next();
            break;
        }
      });

      // Touch events
      this.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: true });
      this.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: true });
      this.addEventListener('touchend', (e) => this._onTouchEnd(e));

      // Prevent slide-link navigation during swipe gestures
      this.addEventListener('click', (e) => {
        if (e.target.closest('.hero-carousel__slide-link') && this._swiped) {
          e.preventDefault();
        }
        this._swiped = false;
      });
    }

    // ──────────────────────────────
    // Navigation
    // ──────────────────────────────

    _next() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this._goTo(nextIndex);
    }

    _prev() {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this._goTo(prevIndex);
    }

    _goTo(index, immediate = false) {
      if (index === this.currentIndex && !immediate) return;
      if (index < 0 || index >= this.slides.length) return;

      const prevIndex = this.currentIndex;
      this.currentIndex = index;

      // Update slides
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('hero-carousel__slide--active', isActive);
        slide.setAttribute('aria-hidden', !isActive);

        // Manage tabindex on interactive elements within slides
        const focusables = slide.querySelectorAll('a, button, [tabindex]');
        focusables.forEach((el) => {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });

      // Update dots
      this.dots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle('hero-carousel__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive);
      });

      // Propagate active slide's text-mode (light/dark) to the root so
      // sibling controls (arrows, counter, etc.) outside the slide can restyle.
      // The light modifier is class-based on the slide; we surface it as a
      // data attribute on the root for unambiguous CSS selectors.
      const activeSlide = this.slides[index];
      const isLight = activeSlide.classList.contains('hero-carousel__slide--light');
      this.setAttribute('data-text-mode', isLight ? 'light' : 'dark');

      // Update slide counter
      const counter = this.querySelector('.hero-carousel__slide-counter');
      if (counter) {
        counter.textContent = `${index + 1}/${this.slides.length}`;
      }

      // Update aria-live region
      if (this.liveRegion && !immediate) {
        const slideLabel = this.slides[index].getAttribute('aria-label') || '';
        this.liveRegion.textContent = slideLabel;
      }

      // Restart autoplay timer if running
      if (!this.isPaused && this.autoplay && !this.reducedMotion) {
        this._startAutoplay();
      }

      // Restart progress animation
      this._startProgress();
    }

    // ──────────────────────────────
    // Autoplay
    // ──────────────────────────────

    _startAutoplay() {
      this._stopAutoplay();
      this.isPaused = false;
      this.autoplayTimer = setInterval(() => this._next(), this.speed);
      this._startProgress();
      this._updatePauseBtn();
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this._stopProgress();
    }

    _pause() {
      this.isPaused = true;
      this._stopAutoplay();
      this._updatePauseBtn();
    }

    _resume() {
      if (this.reducedMotion || !this.autoplay) return;
      this.isPaused = false;
      this._startAutoplay();
      this._updatePauseBtn();
    }

    _togglePause() {
      if (this.isPaused) {
        this.isUserPaused = false;
        this._resume();
      } else {
        this.isUserPaused = true;
        this._pause();
      }
    }

    _updatePauseBtn() {
      if (!this.pauseBtn) return;
      const pauseIcon = this.pauseBtn.querySelector('.hero-carousel__pause-icon');
      const playIcon = this.pauseBtn.querySelector('.hero-carousel__play-icon');

      if (pauseIcon) pauseIcon.style.display = this.isPaused ? 'none' : 'block';
      if (playIcon) playIcon.style.display = this.isPaused ? 'block' : 'none';

      const label = this.isPaused
        ? this.pauseBtn.dataset.playLabel || 'Play'
        : this.pauseBtn.dataset.pauseLabel || 'Pause';
      this.pauseBtn.setAttribute('aria-label', label);
      this.pauseBtn.setAttribute('aria-pressed', this.isPaused);

      // Toggle aria-live: 'off' during auto-rotation, 'polite' when paused
      if (this.liveRegion) {
        this.liveRegion.setAttribute('aria-live', this.isPaused ? 'polite' : 'off');
      }
    }

    // ──────────────────────────────
    // Progress bar on dots
    // ──────────────────────────────

    _startProgress() {
      this._stopProgress();
      if (this.isPaused || this.reducedMotion) return;

      this.progressStart = Date.now();
      const tick = () => {
        const elapsed = Date.now() - this.progressStart;
        const progress = Math.min(elapsed / this.speed, 1);

        // Update active dot's progress bar via CSS custom property
        const activeDot = this.dots[this.currentIndex];
        if (activeDot) {
          const progressBar = activeDot.querySelector('.hero-carousel__dot-progress');
          if (progressBar) {
            progressBar.style.transform = `translate(-50%, -50%) scaleX(${progress})`;
          }
        }

        if (progress < 1) {
          this.progressRaf = requestAnimationFrame(tick);
        }
      };

      // Reset all dot progress bars
      this.dots.forEach((dot) => {
        const bar = dot.querySelector('.hero-carousel__dot-progress');
        if (bar) bar.style.transform = 'translate(-50%, -50%) scaleX(0)';
      });

      this.progressRaf = requestAnimationFrame(tick);
    }

    _stopProgress() {
      if (this.progressRaf) {
        cancelAnimationFrame(this.progressRaf);
        this.progressRaf = null;
      }
    }

    // ──────────────────────────────
    // Touch / Swipe
    // ──────────────────────────────

    _onTouchStart(e) {
      if (e.touches.length !== 1) return;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchDeltaX = 0;
    }

    _onTouchMove(e) {
      if (e.touches.length !== 1) return;
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
    }

    _onTouchEnd(e) {
      const deltaY = Math.abs((e.changedTouches[0]?.clientY || 0) - this.touchStartY);

      // Only swipe if horizontal movement is dominant
      if (Math.abs(this.touchDeltaX) > this.swipeThreshold && Math.abs(this.touchDeltaX) > deltaY) {
        this._swiped = true;
        if (this.touchDeltaX > 0) {
          this._prev();
        } else {
          this._next();
        }
      }
      this.touchDeltaX = 0;
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
}
